import { Box, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import useRolePermissions from '../../../../hooks/useRolePermissions/useRolePermissions';
import usePortalConfig from '../../../../hooks/usePortalConfig/usePortalConfig';

import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import { DeviceOperationsType } from '../../../../models/UserDevicesModels';
import { UserOperationsType } from '../../../../models/UsersModels';

import MenuItemWrapper from '../../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../../components/Menu/MenuStyle';
import { RootState } from '../../../../stores/Store';

interface DeviceDetailsOptionsProps {
  deviceOperations: DeviceOperationsType[];
  ongoingOperations: boolean;
  formOpen: boolean;
  editDevice: () => void;
  assignSim?: () => void;
  deleteDevice: () => void;
  isPerformingAction: boolean;
}
function DeviceDetailsOptions({
  deviceOperations,
  ongoingOperations,
  formOpen,
  editDevice,
  assignSim,
  deleteDevice,
  isPerformingAction,
}: DeviceDetailsOptionsProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const user = useContext(UserDetailsContext);
  const { userOperations } = useSelector((state: RootState) => state.eSimManagerReducer);
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Render edit info option
   */
  const renderEditInfoOption = () => {
    if (isUserAllowed(routePermissions.editDevices)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            editDevice();
          }}
          disabled={user === null || ongoingOperations}
        >
          {t('pages.deviceDetails.editInformation')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render assign sim option
   */
  const renderAssignSimOption = () => {
    if (userOperations.includes(UserOperationsType.CAN_ASSIGN_SIM_TO_USER) && assignSim) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            assignSim();
          }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.assignSim.title')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Render delete option
   */
  const renderDeleteOption = () => {
    if (deviceOperations.includes(DeviceOperationsType.CAN_DELETE)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
            deleteDevice();
          }}
          sx={{ color: theme.palette.primary.main }}
          disabled={isPerformingAction || sessionStorage.getItem('impersonateId') !== null}
        >
          {t('pages.deviceDetails.deleteDevice')}
        </MenuItemWrapper>
      );
    }
    return null;
  };

  /**
   * Can render options
   */
  const renderOptions = () =>
    !formOpen &&
    (isUserAllowed(routePermissions.editDevices) ||
      userOperations.includes(UserOperationsType.CAN_ASSIGN_SIM_TO_USER) ||
      deviceOperations.includes(DeviceOperationsType.CAN_DELETE));

  return (
    <>
      {renderOptions() ? (
        <Box
          id={`box-${user?.id}`}
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0.375rem',
            width: '2.25rem',
            height: '2.25rem',
            flexShrink: 0,
            ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
          }}
          onClick={handleClick}
        >
          <VerticalDotsOutlineIcon />
        </Box>
      ) : null}
      <Box>
        <MenuWrapper
          id={`menu-${user?.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiPaper-root': {
              minWidth: '11.875rem',
            },
          }}
        >
          {renderEditInfoOption()}
          {renderAssignSimOption()}
          {renderDeleteOption()}
        </MenuWrapper>
      </Box>
    </>
  );
}

export default DeviceDetailsOptions;
